'use client';

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import {
  IconBell,
  IconCheck,
  IconLoader2,
  IconRefresh,
  IconSparkles,
  IconTrophy,
  IconVideo,
  IconX,
} from '@tabler/icons-react';
import { useRouter } from '@/i18n/routing';
import {
  formatFeaturedRewardWorkId,
  getFeaturedRewardTarget,
  rememberFeaturedRewardReturnLocation,
} from '@/lib/social/featured-reward-notice';

const TYPE_COPY = {
  'generation.succeeded': {
    key: 'generationSucceeded',
    Icon: IconVideo,
  },
  'generation.failed': {
    key: 'generationFailed',
    Icon: IconVideo,
  },
  'post.featured': {
    key: 'postFeatured',
    Icon: IconSparkles,
  },
  'content.moderation': {
    key: 'contentModeration',
    Icon: IconCheck,
  },
};

function relativeTime(t, value) {
  const created = new Date(value).getTime();
  if (!Number.isFinite(created)) return '';
  const minutes = Math.max(1, Math.round((Date.now() - created) / 60000));
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  return t('notifications.daysAgo', { count: Math.round(hours / 24) });
}

function compactTime(value) {
  const created = new Date(value).getTime();
  if (!Number.isFinite(created)) return '';
  const minutes = Math.max(1, Math.floor((Date.now() - created) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function compactWorkSummary(notification, fallback) {
  const prompt = String(notification?.prompt || '').trim().replace(/\s+/gu, ' ');
  const time = compactTime(notification?.createdAt);
  return {
    text: prompt || fallback,
    time,
  };
}

function copyFor(t, notification) {
  const rewardCredits = Number(notification?.payload?.rewardCredits || 0);
  if (notification.type === 'post.featured' && rewardCredits > 0) {
    return {
      title: t('notifications.featuredRewardTitle', { count: rewardCredits }),
      body: t('notifications.featuredRewardBody', {
        workId: formatFeaturedRewardWorkId(notification),
      }),
      Icon: IconTrophy,
    };
  }
  const copy = TYPE_COPY[notification.type];
  if (!copy) return {
    title: t('notifications.defaultTitle'),
    body: t('notifications.defaultBody'),
    Icon: IconBell,
  };
  return {
    title: t(`notifications.types.${copy.key}.title`),
    body: t(`notifications.types.${copy.key}.body`),
    Icon: copy.Icon,
  };
}

export default function NotificationPanel({ open, onClose, center, side = 'left' }) {
  const t = useTranslations('GateD');
  const router = useRouter();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!open || typeof document === 'undefined') return null;

  const openNotification = async (notification) => {
    await center.markRead(notification.id);
    const fallback = notification.type.startsWith('generation.') ? '/create' : '/profile';
    const target = notification.type === 'post.featured'
      ? getFeaturedRewardTarget(notification)
      : notification.deepLink || fallback;
    if (notification.type === 'post.featured') {
      rememberFeaturedRewardReturnLocation(notification);
    }
    onClose();
    router.push(target);
  };

  return createPortal(
    <>
      <button
        type="button"
        aria-label={t('notifications.close')}
        className="fixed inset-0 z-[110] bg-black/55 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-panel-title"
        className={`fixed bottom-3 top-3 z-[120] flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111419]/98 shadow-[0_24px_100px_rgba(0,0,0,.6)] backdrop-blur-xl ${side === 'right' ? 'right-3 w-[min(390px,calc(100vw-24px))]' : 'left-[86px] w-[min(390px,calc(100vw-102px))]'}`}
      >
        <header className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h2 id="notification-panel-title" className="text-base font-semibold text-white">{t('notifications.title')}</h2>
            <p className="mt-0.5 text-xs text-white/45">{t('notifications.subtitle')}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => center.markAllRead()}
              disabled={!center.unreadCount}
              className="rounded-lg px-2.5 py-2 text-xs font-medium text-amber-300 transition hover:bg-amber-300/10 disabled:cursor-default disabled:text-white/25"
            >
              {t('notifications.markAllRead')}
            </button>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white" aria-label={t('common.close')}>
              <IconX size={18} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-color:rgba(255,255,255,.22)_transparent] [scrollbar-width:thin]">
          {center.status === 'loading' && center.items.length === 0 ? (
            <div className="grid h-full min-h-64 place-items-center text-white/45">
              <IconLoader2 size={22} className="animate-spin motion-reduce:animate-none" />
            </div>
          ) : center.status === 'error' && center.items.length === 0 ? (
            <div className="flex h-full min-h-64 flex-col items-center justify-center px-7 text-center">
              <IconRefresh size={24} className="text-red-300" />
              <p className="mt-3 text-sm text-white/60">{center.error}</p>
              <button type="button" onClick={() => center.load()} className="mt-4 rounded-full bg-white px-4 py-2 text-sm font-medium text-black">{t('common.retry')}</button>
            </div>
          ) : center.items.length === 0 ? (
            <div className="flex h-full min-h-64 flex-col items-center justify-center px-7 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full border border-white/10 bg-white/5 text-white/45"><IconBell size={21} /></span>
              <p className="mt-4 text-sm font-medium text-white/75">{t('notifications.emptyTitle')}</p>
              <p className="mt-1 text-xs leading-5 text-white/40">{t('notifications.emptyBody')}</p>
            </div>
          ) : (
            <div role="list">
              {center.items.map((notification) => {
                const { title, body, Icon } = copyFor(t, notification);
                const rewardCredits = Number(notification?.payload?.rewardCredits || 0);
                const isFeaturedReward = notification.type === 'post.featured' && rewardCredits > 0;
                const usesCompactWorkSummary = notification.type === 'generation.succeeded'
                  || isFeaturedReward;
                const workSummary = compactWorkSummary(notification, body);
                const rewardCreditLabel = t('notifications.credits');
                return (
                  <button
                    key={notification.id}
                    type="button"
                    role="listitem"
                    onClick={() => openNotification(notification)}
                    className={`relative flex w-full gap-3 border-b px-5 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-300/60 ${isFeaturedReward ? 'border-amber-300/20 bg-[linear-gradient(110deg,rgba(245,158,11,.18),rgba(245,158,11,.035))] hover:bg-amber-300/[0.12]' : 'border-white/[0.07] hover:bg-white/[0.045]'}`}
                  >
                    <span className={`grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full border text-amber-300 ${notification.actor?.avatarUrl ? 'border-white/10 bg-transparent' : isFeaturedReward ? 'border-amber-300/45 bg-amber-300/15 shadow-[0_0_26px_rgba(251,191,36,.22)]' : 'border-white/10 bg-white/[0.06]'}`}>
                      {notification.actor?.avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={notification.actor.avatarUrl} alt="" className="h-full w-full object-cover" />
                      ) : <Icon size={18} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="block whitespace-nowrap text-[13px] font-medium text-white">{title}</span>
                        {isFeaturedReward ? (
                          <span className="rounded-full border border-amber-200/25 bg-amber-300 px-2 py-0.5 text-[10px] font-bold text-[#281902]">
                            + {rewardCredits} {rewardCreditLabel}
                          </span>
                        ) : null}
                      </span>
                      {usesCompactWorkSummary ? (
                        <span className="mt-1 flex min-w-0 items-center gap-1 text-xs leading-5 text-white/50">
                          <span className="min-w-0 flex-1 truncate">{workSummary.text}</span>
                          {workSummary.time ? (
                            <span className="shrink-0 text-white/35">· {workSummary.time}</span>
                          ) : null}
                        </span>
                      ) : (
                        <>
                          <span className="mt-1 line-clamp-2 block text-xs leading-5 text-white/50">{body}</span>
                          <span className="mt-1.5 block text-[11px] text-white/30">{relativeTime(t, notification.createdAt)}</span>
                        </>
                      )}
                    </span>
                    {!notification.readAt ? <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,.6)]" /> : null}
                  </button>
                );
              })}
              {center.nextCursor ? (
                <div className="p-4 text-center">
                  <button type="button" onClick={center.loadMore} disabled={center.status === 'loading-more'} className="rounded-full border border-white/10 px-4 py-2 text-xs text-white/60 transition hover:bg-white/5 hover:text-white disabled:opacity-50">
                    {center.status === 'loading-more' ? t('common.loading') : t('notifications.loadMore')}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>
    </>,
    document.body,
  );
}
